"use client"

import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import { Copy, RefreshCw } from "lucide-react"
import { toast } from "sonner"

const LOREM_WORDS = [
  "lorem", "ipsum", "dolor", "sit", "amet", "consectetur", "adipiscing", "elit",
  "sed", "do", "eiusmod", "tempor", "incididunt", "ut", "labore", "et", "dolore",
  "magna", "aliqua", "enim", "ad", "minim", "veniam", "quis", "nostrud",
  "exercitation", "ullamco", "laboris", "nisi", "aliquip", "ex", "ea", "commodo",
  "consequat", "duis", "aute", "irure", "in", "reprehenderit", "voluptate",
  "velit", "esse", "cillum", "fugiat", "nulla", "pariatur", "excepteur", "sint",
  "occaecat", "cupidatat", "non", "proident", "sunt", "culpa", "qui", "officia",
  "deserunt", "mollit", "anim", "id", "est", "laborum", "perspiciatis", "unde",
  "omnis", "iste", "natus", "error", "voluptatem", "accusantium", "doloremque",
  "laudantium", "totam", "rem", "aperiam", "eaque", "ipsa", "quae", "ab", "illo",
  "inventore", "veritatis", "quasi", "architecto", "beatae", "vitae", "dicta",
  "explicabo", "nemo", "ipsam", "quia", "voluptas", "aspernatur", "aut", "odit",
  "fugit", "consequuntur", "magni", "dolores", "eos", "ratione", "sequi",
  "nesciunt", "neque", "porro", "quisquam", "dolorem", "adipisci", "numquam",
  "eius", "modi", "tempora", "incidunt", "magnam", "quaerat"
]

type UnitType = "paragraphs" | "sentences" | "words"

export function LoremIpsumGenerator() {
  const [amount, setAmount] = useState(3)
  const [unitType, setUnitType] = useState<UnitType>("paragraphs")
  const [startWithLorem, setStartWithLorem] = useState(true)
  const [output, setOutput] = useState("")

  const getRandomWord = useCallback(() => {
    return LOREM_WORDS[Math.floor(Math.random() * LOREM_WORDS.length)]
  }, [])

  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

  const generateSentence = useCallback(
    (minWords = 8, maxWords = 15, startWord?: string) => {
      const wordCount = Math.floor(Math.random() * (maxWords - minWords + 1)) + minWords
      const words: string[] = []

      if (startWord) {
        words.push(startWord)
      }

      while (words.length < wordCount) {
        words.push(getRandomWord())
      }

      words[0] = capitalize(words[0])
      return words.join(" ") + "."
    },
    [getRandomWord]
  )

  const generateParagraph = useCallback(
    (sentenceCount = 4, startWord?: string) => {
      const sentences: string[] = []
      const count = Math.floor(Math.random() * 3) + sentenceCount

      for (let i = 0; i < count; i++) {
        if (i === 0 && startWord) {
          sentences.push(generateSentence(8, 15, startWord))
        } else {
          sentences.push(generateSentence())
        }
      }

      return sentences.join(" ")
    },
    [generateSentence]
  )

  const generate = useCallback(() => {
    let result = ""

    switch (unitType) {
      case "paragraphs": {
        const paragraphs: string[] = []
        for (let i = 0; i < amount; i++) {
          if (i === 0 && startWithLorem) {
            paragraphs.push(generateParagraph(4, "Lorem"))
          } else {
            paragraphs.push(generateParagraph())
          }
        }
        result = paragraphs.join("\n\n")
        break
      }
      case "sentences": {
        const sentences: string[] = []
        for (let i = 0; i < amount; i++) {
          if (i === 0 && startWithLorem) {
            sentences.push(generateSentence(8, 15, "Lorem"))
          } else {
            sentences.push(generateSentence())
          }
        }
        result = sentences.join(" ")
        break
      }
      case "words": {
        const words: string[] = []
        if (startWithLorem) {
          words.push("Lorem")
        }
        while (words.length < amount) {
          words.push(getRandomWord())
        }
        words[0] = capitalize(words[0])
        result = words.join(" ") + "."
        break
      }
    }

    setOutput(result)
  }, [amount, unitType, startWithLorem, generateParagraph, generateSentence, getRandomWord])

  const copyToClipboard = useCallback(() => {
    if (!output) return
    navigator.clipboard.writeText(output)
    toast.success("Copied to clipboard!")
  }, [output])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generator Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Amount: {amount}</Label>
            <Slider
              value={[amount]}
              onValueChange={([value]) => setAmount(value)}
              min={1}
              max={unitType === "words" ? 500 : 20}
              step={1}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label>Generate</Label>
            <RadioGroup
              value={unitType}
              onValueChange={(v) => {
                setUnitType(v as UnitType)
                if (v === "words" && amount > 500) setAmount(100)
                if (v !== "words" && amount > 20) setAmount(5)
              }}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="paragraphs" id="paragraphs" />
                <Label htmlFor="paragraphs">Paragraphs</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="sentences" id="sentences" />
                <Label htmlFor="sentences">Sentences</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="words" id="words" />
                <Label htmlFor="words">Words</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="startWithLorem"
              checked={startWithLorem}
              onCheckedChange={(checked) => setStartWithLorem(checked as boolean)}
            />
            <Label htmlFor="startWithLorem">Start with &quot;Lorem ipsum...&quot;</Label>
          </div>

          <Button onClick={generate} className="w-full">
            <RefreshCw className="w-4 h-4 mr-2" />
            Generate
          </Button>
        </CardContent>
      </Card>

      {output && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Generated Text</CardTitle>
              <Button variant="outline" size="sm" onClick={copyToClipboard}>
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              className="min-h-[200px]"
            />
            <p className="text-sm text-muted-foreground mt-2">
              {output.split(/\s+/).length} words, {output.length} characters
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
